<template lang="pug">
.locations-map
	.locations-map__inner
		.locations-map__map 
		button.locations-map__to-top(
			:class="{showing: listScrolled && !nearBottom}",
			@click.prevent="scrollToTop"
		) {{ currentPageWidth < breakpoint ? 'Top' : 'Back to Top' }}
</template>

<script>
import mapInit from '../../global/mixins/gMapsInit';

import { mapGetters, mapMutations } from "vuex";

export default {
	name: "Map",
	components: {},
	data() {
		return {
			listScrolled: false,
			nearBottom: false,
			breakpoint: 992,
			currentPageWidth: null,
			pageEl: null,
			scrolling: false,
			markers: [],
			infoWindow: null,
			activeMarker: null,
			latLngBounds: null,
		};
	},
	computed: {
		...mapGetters([
			"activeLocations",
			"mapOptions",
			"lat",
			"lng",
			"google",
			"apiKey",
			"map",
		]),
	},
	methods: {
		...mapMutations([
			"setStateValue"
		]),
		infoWindowMarkup(location) {
			// used to generate info windows that appear when a marker is clicked
			return `
				<div class="locations-map__info-window">
					<h6>
						<a href="${location.url}">${location.title}</a>
					</h6>
					<address>
						<div>${location.address.street}</div>
						<div>${location.address.city}, ${location.address.state} ${location.address.zipCode}</div>
					</address>
					<a href="tel:${location.phoneNumber}">${location.phoneNumber}</a>
				</div>
			`
		},
		openInfoWindow(marker) {
			// close any open info windows first
			this.markers.forEach(m => m.__meta._infoWindow.close());
			this.activeMarker = marker;
			this.activeMarker.__meta._infoWindow.open({
				anchor: this.activeMarker,
				map: this.map,
				shouldFocus: true,
			});
		},
		// Below function also triggered in App.vue file inside watcher
		async addMarkers() {
			this.latLngBounds = null;
			this.markers.forEach(m => m.setMap(null));
			this.markers = [];
			// latLngBounds object used for fitBounds() method to auto center and auto zoom around showing markers
			this.latLngBounds = new this.google.maps.LatLngBounds;
			let labels = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
			this.activeLocations.forEach(async (location, index) => {
				await new Promise((resolve) => {
					const createMarker = () => {
						let _infoWindow = new this.google.maps.InfoWindow({
							content: this.infoWindowMarkup(location),
						});
						const marker = new this.google.maps.Marker({
							map: this.map,
							id: location.url,
							animation: null,
							label: {
								text: labels[index],
								color: this.mapOptions.markerLabel.color,
								fontFamily: this.mapOptions.markerLabel.fontFamily,
								fontSize: this.mapOptions.markerLabel.fontSize,
								fontWeight: this.mapOptions.markerLabel.fontWeight,
								className: 'marker-label'
							},
							icon: {
								url: this.mapOptions.markerIcon.url,
								scaledSize: new google.maps.Size(32, 48),
							},
							position: {
								lat: parseFloat(location.coordinate.lat),
								lng: parseFloat(location.coordinate.lng),
							},
							__meta: {
								id: location.url,
								_infoWindow: _infoWindow,
								...location,
							},
						});

						this.markers.push(marker);
						// add listener for marker click to open the info window
						marker.addListener("click", () => {this.openInfoWindow(marker)});
						// this is where we prepare the latLngBounds object for the fitBounds() method used below, by extending the boundary to include each new marker we create
						this.latLngBounds.extend(new this.google.maps.LatLng(marker.position.lat(), marker.position.lng()));
						
						resolve(marker);
					};
					createMarker();
				});
			});
			this.map.fitBounds(this.latLngBounds);
		},
		async init() {
			// google initialized in store/index.js -> used by map and search header
			let mapOptions = {
				mapTypeId: this.google.maps.MapTypeId.ROADMAP,
				clickableIcons: false,
				gestureHandling: "cooperative",
				clickableIcons: false,
				// set large zoom level because we will use fitBounds() method when adding map markers
				zoom: 20,
				center: new this.google.maps.LatLng(
					this.lat,
					this.lng
				),
			}
			let map = new google.maps.Map(this.$el.querySelector(".locations-map__map"), mapOptions);
			this.setStateValue(['map', map]);
			this.$emit("mapSet");

			// info window appears when clicking a map marker to see location details/links
			this.infoWindow = new this.google.maps.InfoWindow({
				content: this.$el.querySelector(".locations-map__info-window"),
			});
			this.infoWindow.addListener("closeclick", () => {
				this.activeMarker = null;
			});

			let service = new this.google.maps.places.PlacesService(this.map);
			this.setStateValue(['placesService', service]);
		},
		handlePageResize() {
			let newPageWidth = this.pageEl.offsetWidth;
			if (this.currentPageWidth < this.breakpoint && newPageWidth >= this.breakpoint) {
				this.pageEl.style.overflowX = "visible";
				if (!this.map) {
					this.init();
					this.addMarkers();
				}
			} else if (this.currentPageWidth >= this.breakpoint && newPageWidth < this.breakpoint) {
				this.pageEl.style.overflowX = "hidden";
			}
			this.currentPageWidth = newPageWidth;
		},
		scrollToTop() {
			window.scroll({
				top: 0,
				behavior: "smooth"
			});
		},
	},
	mounted() {
		this.pageEl = this.$el.closest(".page");
		this.currentPageWidth = this.pageEl.offsetWidth;
		if (this.pageEl.offsetWidth >= this.breakpoint) this.pageEl.style.overflowX = "visible";
		window.addEventListener("resize", this.handlePageResize);
		let scrollInterval = setInterval(() => {
			if (this.scrolling) {
				this.listScrolled = this.$el.getBoundingClientRect().top < 20;
				this.nearBottom = this.$el.getBoundingClientRect().bottom < 200;
			}
		}, 100);
		window.addEventListener("scroll", () => {this.scrolling = true});
		this.init();
	},
};
</script>


<style scoped lang="scss">
.locations-map {
	flex: 1;
	align-self: stretch;
	height: auto;
}
</style>

<style lang="scss">
.marker-label {
	position: relative;
	bottom: 6px;
}
.locations-map__info-window {
	* {
		text-align: center;
	}
	h6 {
		margin-bottom: 1rem;
		text-transform: none;
		& > a {
			text-decoration: underline;
		}
	}
	address {
		font-style: normal;
		margin-bottom: 0;
	}
	& > a {
		display: block;
		margin: 0 auto;
	}
}
</style>